import type {
  ApActionType,
  ApDayRange,
  ApInvoiceStatus,
  ApReviewState,
} from "@/accounts-payable/types";

// The AP list endpoints' query params, shared by their hooks and the
// invoice export.

export interface ApInvoiceListFilters {
  statuses?: ApInvoiceStatus[];
  search?: string;
  added?: ApDayRange | null;
  reviewed?: ApDayRange | null;
  reviewStates?: ApReviewState[];
  inQuickbooks?: boolean;
  actionTypes?: ApActionType[];
  assigneeIds?: string[];
  vendorIds?: string[];
  invoiceDate?: ApDayRange | null;
}

export const sortedOrNull = <T extends string>(values?: T[]) =>
  values?.length ? [...values].sort() : null;

export const ownerAndActionParams = (filters: {
  actionTypes: ApActionType[] | null;
  assigneeIds: string[] | null;
}) => ({
  ...(filters.actionTypes && { "filter[action_type]": filters.actionTypes }),
  ...(filters.assigneeIds && { "filter[assignee_id]": filters.assigneeIds }),
});

export const dayParams = (
  prefix: "created" | "reviewed" | "invoice_date",
  range: ApDayRange | null,
) =>
  range
    ? {
        [`filter[${prefix}_from]`]: range.from,
        [`filter[${prefix}_to]`]: range.to,
      }
    : {};

export const normalizeInvoiceFilters = (filters: ApInvoiceListFilters) => ({
  statuses: filters.statuses?.length ? [...filters.statuses].sort() : null,
  search: filters.search?.trim() || null,
  added: filters.added ?? null,
  reviewed: filters.reviewed ?? null,
  reviewStates: filters.reviewStates?.length
    ? [...filters.reviewStates].sort()
    : null,
  inQuickbooks: filters.inQuickbooks ?? null,
  actionTypes: sortedOrNull(filters.actionTypes),
  assigneeIds: sortedOrNull(filters.assigneeIds),
  vendorIds: sortedOrNull(filters.vendorIds),
  invoiceDate: filters.invoiceDate ?? null,
});

export const invoiceListParams = (
  normalized: ReturnType<typeof normalizeInvoiceFilters>,
) => ({
  ...(normalized.statuses && { "filter[status]": normalized.statuses }),
  ...(normalized.search && { q: normalized.search }),
  ...dayParams("created", normalized.added),
  ...dayParams("reviewed", normalized.reviewed),
  ...dayParams("invoice_date", normalized.invoiceDate),
  ...(normalized.vendorIds && { "filter[vendor_id]": normalized.vendorIds }),
  ...(normalized.reviewStates && {
    "filter[review_state]": normalized.reviewStates,
  }),
  ...(normalized.inQuickbooks !== null && {
    "filter[in_quickbooks]": String(normalized.inQuickbooks),
  }),
  ...ownerAndActionParams(normalized),
});
